import { observer } from "mobx-react";
import React, { useEffect, useMemo } from "react";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import { Box, Button, Chip, Tooltip, Typography } from "@mui/material";
import { useParams } from "react-router-dom";
import { IRouteParams } from "../../Interfaces";
import { useModal, useStore } from "src/Shared/Hooks";
import { useStyles } from "./CompanyRevenue.styles";
import { CustomDataGrid } from "src/Shared/Components";
import { columns } from "./CompanyRevenue.column";
import { VIEW_MODE } from "src/Shared/Enums/ViewMode.enum";
import AddOrEditCompanyAdditionalRevenue from "../AddOrEditCompanyAdditionalRevenue/AddOrEditCompanyAdditionalRevenue";
import { getTempId } from "src/Shared/Tools";
import { ICompanyAdditionalRevenueStream } from "../../Interfaces/ICompanyAdditionalRevenueStream";
import WarningPopup from "src/Shared/Components/WarningPopup/WarningPopup";
import CompanyRevenueService from "../../Services/CompanyRevenue.service";
import ToastService from "src/Shared/Services/Toast.service";
import { AxiosError } from "axios";
import { finalize } from "rxjs";
import { IResult } from "src/Shared/Interfaces";
import Title from "src/Shared/Components/Title/Title";
import { isClientArchived } from "../../Tools/ArchivedClient";

interface CompanyRevenueProps {
  title?: string;
}

const CompanyRevenue: React.FC<CompanyRevenueProps> = ({ title }) => {
  const classes = useStyles();
  const modal = useModal();
  const { companyRowID } = useParams<Partial<IRouteParams>>();

  const {
    rootStore: {
      companyRevenueStore: {
        getCompanyRevenue,
        companyRevenueData,
        gridLoading,
      },
      commonStore: { setWarningPopupLoading },
      companyStore: { companyOverview },
    },
  } = useStore();

  // Reachable at this route even with the sidebar link locked (a stale tab, a shared link, a direct
  // URL) — the write is refused server-side either way, but the button should not invite it (task
  // 5844).
  const archived = isClientArchived(companyOverview);

  useEffect(() => {
    if (companyRowID) {
      getCompanyRevenue(companyRowID);
    }
  }, [companyRowID]);

  const addManualRevenue = () => {
    modal.open(
      <AddOrEditCompanyAdditionalRevenue
        mode={VIEW_MODE.ADD}
        companyRowID={companyRowID}
      />
    );
  };

  const editManualRevenue = (
    additionalRevenue: ICompanyAdditionalRevenueStream
  ) => {
    modal.open(
      <AddOrEditCompanyAdditionalRevenue
        mode={VIEW_MODE.EDIT}
        companyRowID={companyRowID}
        additionalRevenue={additionalRevenue}
      />
    );
  };

  const onDeleteAdditionalRevenue = (
    additionalRevenue: ICompanyAdditionalRevenueStream
  ) => {
    setWarningPopupLoading(true);
    CompanyRevenueService.deleteCompanyAdditionalRevenueStream(
      additionalRevenue.companyAdditionalRevenueStreamID
    )
      .pipe(
        finalize(() => {
          setWarningPopupLoading(false);
        })
      )
      .subscribe({
        next: (data: IResult<string>) => {
          ToastService.success(data.message);
          getCompanyRevenue(companyRowID);
          modal.close();
        },
        error: (error: AxiosError) => ToastService.axiosError(error),
      });
  };

  const deleteAdditionalRevenue = (
    additionalRevenue: ICompanyAdditionalRevenueStream
  ) => {
    modal.open(
      <WarningPopup
        onConfirm={(e) => {
          if (e) {
            onDeleteAdditionalRevenue(additionalRevenue);
          } else {
            modal.close();
          }
        }}
        message="Are you sure you want delete additional revenue?"
        defaultClose={false}
      />
    );
  };

  const revenueSource = useMemo(() => {
    if (companyRevenueData && companyRevenueData.length > 0) {
      return companyRevenueData[0].revenueSource;
    }
    return null;
  }, [companyRevenueData]);

  const getSourceBannerInfo = () => {
    if (revenueSource === "Xero") {
      return {
        text: "Revenue source: Xero",
        tooltip: "Revenue figures are sourced from Xero charges",
        className: classes.revenueSourceXero,
      };
    }
    if (revenueSource === "Imperans") {
      return {
        text: "Revenue source: Imperans estimate",
        tooltip: "Revenue figures are estimated based on completed tests and Imperans base pricing",
        className: classes.revenueSourceImperans,
      };
    }
    return null;
  };

  const sourceBannerInfo = getSourceBannerInfo();

  return (
    <Box className={classes.root}>
      <Box className={classes.viewButtons}>
        <Box className={classes.headerLeft}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Title
              backButtonEnabled={false}
              title={title || "Estimated Annual Revenue"}
              variant="h6"
            />
            {sourceBannerInfo && (
              <Tooltip title={sourceBannerInfo.tooltip} arrow>
                <Chip
                  label={sourceBannerInfo.text}
                  size="small"
                  className={`${classes.revenueSourceBanner} ${sourceBannerInfo.className}`}
                />
              </Tooltip>
            )}
          </Box>
          <Box sx={{ color: "#888580", fontSize: "13px" }}>
            <Typography variant="body1">
              Manage estimated annual revenue and additional revenue streams
            </Typography>
          </Box>
        </Box>

        {!archived && (
          <Box className={classes.addNewRevenue}>
            <Button variant="contained" onClick={addManualRevenue}>
              <AddCircleIcon />
              Add Manual Revenue
            </Button>
          </Box>
        )}
      </Box>

      <CustomDataGrid
        columns={columns(editManualRevenue, deleteAdditionalRevenue)}
        loading={gridLoading}
        rowSelection={false}
        rows={companyRevenueData || []}
        rowCount={companyRevenueData?.length || 0}
        rowHeight={70}
        style={{
          height: "calc(100vh - 255px)",
        }}
        getRowId={(row) => row.companyAdditionalRevenueStreamID || getTempId()}
        hideFooterPagination
        hideFooterSelectedRowCount
      />
    </Box>
  );
};

export default observer(CompanyRevenue);
